import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import hybridBlogService from "../services/hybridBlogService";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import {
  BlogPostHero,
  BlogPostContent,
  RelatedPosts,
} from "../components/blogPost";
import { SEO, generateArticleSchema, generateBreadcrumbSchema } from "../seo";
import "../styles/blogPost/BlogPost.css";

const BlogPost = () => {
  const { id } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const blogData = await hybridBlogService.fetchBlogBySlug(id);

        // Transform to match the BlogPost component's expected format
        const transformedPost = {
          id: blogData.id,
          slug: blogData.slug || id,
          title: blogData.title,
          subtitle: blogData.excerpt,
          author: blogData.author,
          date: hybridBlogService.formatDate(blogData.date),
          readTime: blogData.readTime,
          category: blogData.category,
          heroImage: blogData.image,
          content: blogData.content,
          tags: blogData.tags || []
        };

        setBlogPost(transformedPost);
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Blog post not found or failed to load.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadBlogPost();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <Header />
        <main className="blog-post-main">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div>Loading blog post...</div>
          </div>
        </main>
        <CheckoutFooter />
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div className="blog-post-page">
        <Header />
        <main className="blog-post-main">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div>{error || 'Blog post not found'}</div>
          </div>
        </main>
        <CheckoutFooter />
      </div>
    );
  }

  // SEO Schemas
  const articleSchema = generateArticleSchema({
    title: blogPost.title,
    excerpt: blogPost.subtitle,
    image: blogPost.heroImage,
    publishedDate: blogPost.date,
    author: blogPost.author,
    slug: blogPost.slug,
    tags: blogPost.tags,
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '' },
    { name: 'Blog', path: '/blog' },
    { name: blogPost.title, path: `/blog/${blogPost.slug}` },
  ]);

  return (
    <div className="blog-post-page">
      <SEO
        title={`${blogPost.title} | SunCatchers Blog`}
        description={blogPost.subtitle}
        image={blogPost.heroImage}
        type="article"
        article={{
          publishedDate: blogPost.date,
          author: blogPost.author,
          tags: blogPost.tags,
        }}
        schema={[articleSchema, breadcrumbSchema]}
      />
      <h1 className="sr-only">{blogPost.title}</h1>
      <Header />
      <main className="blog-post-main">
        <BlogPostHero post={blogPost} />
        <BlogPostContent post={blogPost} />
        <RelatedPosts currentPost={blogPost} />
      </main>
      <CheckoutFooter />
    </div>
  );
};

export default BlogPost;
